import { Component, OnDestroy, OnInit, inject, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FilterType } from '../services/video-room/video-filter.service';
import { Store } from '@ngrx/store';
import { coreSelectors } from 'app/core/state';
import { AppState } from 'app/types';
import { ReplaySubject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-filter-controls',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="filter-controls">
      <div class="filter-panel">
        <h3>Video Filters</h3>

        <div class="filter-tabs">
          <button
            class="tab-button"
            [class.active]="filterCategory === 'all'"
            (click)="filterCategory = 'all'; selectedFilter = FilterType.NONE"
          >
            Full Frame
          </button>
          <button
            class="tab-button"
            [class.active]="filterCategory === 'background'"
            (click)="filterCategory = 'background'; selectedFilter = FilterType.NONE"
          >
            Background Only
          </button>
        </div>

        <div class="filter-options">
          <div
            *ngFor="let filter of currentFilters"
            class="filter-option"
            [class.active]="selectedFilter === filter.type"
            (click)="selectFilter(filter.type)"
          >
            <div class="filter-preview" [ngClass]="filter.type">
              <div class="preview-icon">👤</div>
            </div>
            <div class="filter-name">{{ filter.name }}</div>
          </div>
        </div>

        <div *ngIf="selectedFilter !== 'none'" class="filter-intensity">
          <label for="intensity">Intensity: {{ intensity.toFixed(1) }}</label>
          <input
            type="range"
            id="intensity"
            min="0"
            max="1"
            step="0.1"
            [(ngModel)]="intensity"
            (ngModelChange)="intensityChanged()"
          />
        </div>

        <div *ngIf="!videoElement() && isBackgroundFilter(selectedFilter)" class="warning">
          Background filters require camera access
        </div>
      </div>
    </div>
  `,
  styles: [
    `
      .filter-controls {
        position: absolute;
        bottom: 90px;
        right: 20px;
        z-index: 100;
        font-family: var(--font-family);
      }

      .filter-panel {
        background-color: rgba(30, 30, 30, 0.85);
        border-radius: 8px;
        padding: 15px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
        backdrop-filter: blur(5px);
        border: 1px solid rgba(255, 255, 255, 0.15);
        color: white;
        max-width: 300px;
        width: 280px;
      }

      h3 {
        margin-top: 0;
        margin-bottom: 15px;
        text-align: center;
        font-size: 16px;
        font-weight: 500;
        color: #fff;
      }

      .filter-tabs {
        display: flex;
        justify-content: space-between;
        margin-bottom: 15px;
        border-radius: 6px;
        overflow: hidden;
        background-color: rgba(0, 0, 0, 0.3);
        border: 1px solid rgba(255, 255, 255, 0.1);
      }

      .tab-button {
        flex: 1;
        background: none;
        border: none;
        color: white;
        cursor: pointer;
        font-size: 14px;
        padding: 8px 12px;
        transition: all 0.2s ease;
        position: relative;
        overflow: hidden;
      }

      .tab-button:hover {
        background: rgba(255, 255, 255, 0.15);
      }

      .tab-button.active {
        background: rgba(var(--primary-color-rgb, 52, 152, 219), 0.6);
        font-weight: 500;
      }

      .tab-button.active::after {
        content: '';
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        height: 2px;
        background-color: rgb(var(--primary-color-rgb, 52, 152, 219));
      }

      .filter-options {
        display: flex;
        flex-wrap: wrap;
        gap: 12px;
        justify-content: center;
        margin-bottom: 15px;
      }

      .filter-option {
        cursor: pointer;
        transition: transform 0.2s ease;
        width: 60px;
        text-align: center;
      }

      .filter-option:hover {
        transform: scale(1.05);
      }

      .filter-option.active {
        outline: 2px solid var(--primary-color);
        border-radius: 5px;
        background-color: rgba(52, 152, 219, 0.2);
      }

      .filter-preview {
        width: 50px;
        height: 50px;
        border-radius: 50%;
        background-color: #555;
        margin: 0 auto 5px;
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
      }

      .preview-icon {
        font-size: 30px;
      }

      .filter-name {
        font-size: 12px;
        white-space: nowrap;
      }

      .filter-intensity {
        margin-top: 15px;
      }

      label {
        display: block;
        margin-bottom: 5px;
        font-size: 14px;
      }

      input[type='range'] {
        width: 100%;
        background-color: rgba(255, 255, 255, 0.2);
        height: 6px;
        border-radius: 3px;
        outline: none;
      }

      /* Filter preview styles */
      .blur .preview-icon {
        filter: blur(3px);
      }

      .pixelate .preview-icon {
        filter: blur(1px);
        image-rendering: pixelated;
      }

      .grayscale .preview-icon {
        filter: grayscale(100%);
      }

      .sepia .preview-icon {
        filter: sepia(100%);
      }

      .invert .preview-icon {
        filter: invert(100%);
      }

      .hueRotate .preview-icon {
        filter: hue-rotate(180deg);
      }

      .brightness .preview-icon {
        filter: brightness(150%);
      }

      .contrast .preview-icon {
        filter: contrast(200%);
      }

      .vintage .preview-icon {
        filter: sepia(50%) contrast(120%) brightness(80%);
      }

      .nightVision .preview-icon {
        filter: brightness(120%) sepia(50%) hue-rotate(90deg) saturate(200%);
        background-color: rgba(0, 255, 0, 0.2);
      }

      /* Face filter preview styles */
      .faceMask .preview-icon {
        position: relative;
      }

      .faceMask .preview-icon::after {
        content: '😷';
        position: absolute;
        font-size: 20px;
        bottom: -5px;
      }

      .faceHorns .preview-icon {
        position: relative;
      }

      .faceHorns .preview-icon::after {
        content: '😈';
        position: absolute;
        font-size: 18px;
        top: -8px;
      }

      .faceHalo .preview-icon {
        position: relative;
      }

      .faceHalo .preview-icon::after {
        content: '😇';
        position: absolute;
        font-size: 18px;
        top: -5px;
      }
    `,
  ],
})
export class FilterControlsComponent implements OnInit, OnDestroy {
  // Expose FilterType enum to the template
  public FilterType = FilterType;

  public readonly videoElement = input<HTMLVideoElement>();
  public selectedFilter: FilterType = FilterType.NONE;
  public intensity = 0.5;
  public filterCategory: 'all' | 'background' = 'all';

  public availableFilters = [
    { type: FilterType.NONE, name: 'None' },
    { type: FilterType.BLUR, name: 'Blur' },
    { type: FilterType.PIXELATE, name: 'Pixelate' },
    { type: FilterType.GRAYSCALE, name: 'Grayscale' },
    { type: FilterType.SEPIA, name: 'Sepia' },
    { type: FilterType.INVERT, name: 'Invert' },
    { type: FilterType.HUE_ROTATE, name: 'Hue Shift' },
    { type: FilterType.BRIGHTNESS, name: 'Bright' },
    { type: FilterType.CONTRAST, name: 'Contrast' },
    { type: FilterType.VINTAGE, name: 'Vintage' },
    { type: FilterType.NIGHT_VISION, name: 'Night Vision' },
  ];

  public backgroundFilters = [
    { type: FilterType.NONE, name: 'None' },
    { type: FilterType.BACKGROUND_BLUR, name: 'BG Blur' },
    { type: FilterType.BACKGROUND_REPLACE, name: 'BG Replace' },
    { type: FilterType.FACE_MASK, name: 'Face Mask' },
    { type: FilterType.FACE_HORNS, name: 'Horns' },
    { type: FilterType.FACE_HALO, name: 'Halo' },
  ];

  public get currentFilters(): { type: FilterType; name: string }[] {
    return this.filterCategory === 'all' ? this.availableFilters : this.backgroundFilters;
  }

  private userId: string | undefined = undefined;
  private destroyed$ = new ReplaySubject<boolean>(1);

  public constructor() {
    const store = inject<Store<AppState>>(Store);

    store
      .select(coreSelectors.getUserEntity)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((user) => (this.userId = user?.userId));
  }

  public ngOnInit(): void {
    // Initialize with no filter
    this.selectedFilter = FilterType.NONE;
  }

  public selectFilter(filterType: FilterType): void {
    this.selectedFilter = filterType;
    this.applyCurrentFilter();
  }

  public intensityChanged(): void {
    this.applyCurrentFilter();
  }

  private applyCurrentFilter(): void {
    // Emit an event that will be caught by the parent component
    const event = new CustomEvent('filter-changed', {
      detail: {
        userId: this.userId,
        filterType: this.selectedFilter,
        intensity: this.intensity,
      },
      bubbles: true,
    });

    document.dispatchEvent(event);
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  public isBackgroundFilter(filter: FilterType): boolean {
    return (
      filter === FilterType.BACKGROUND_BLUR ||
      filter === FilterType.BACKGROUND_REPLACE ||
      filter === FilterType.FACE_MASK ||
      filter === FilterType.FACE_HORNS ||
      filter === FilterType.FACE_HALO
    );
  }
}
